import { z } from 'zod';
import { BillingPeriodSchema, ChargeTypeSchema, CurrencyCodeSchema, EntityStatusSchema, PlanCategorySchema, PlanFeatureTypeSchema, PlanTypeSchema, PricingModelSchema, TargetAudienceSchema, } from '../common/enums.js';
import { MetadataSchema, PaginationQuerySchema } from '../common/types.js';
export const PlanSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    name: z.string(),
    code: z.string(),
    description: z.string().nullable(),
    target_audience: TargetAudienceSchema,
    currency: CurrencyCodeSchema,
    category: PlanCategorySchema,
    plan_type: PlanTypeSchema,
    billing_period: BillingPeriodSchema,
    trial_available: z.boolean(),
    trial_days: z.number().int().min(0),
    status: EntityStatusSchema,
    version: z.number().int(),
    parent_plan_id: z.string().nullable(),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const PlanChargeSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    plan_id: z.string(),
    charge_name: z.string(),
    charge_code: z.string(),
    description: z.string().nullable(),
    charge_type: ChargeTypeSchema,
    billing_frequency: BillingPeriodSchema.nullable(),
    amount: z.number(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const PlanAgentActionSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    plan_id: z.string(),
    agent_action_id: z.string(),
    billable_signal_id: z.string(),
    pricing_model: PricingModelSchema,
    unit_price: z.number().nullable(),
    package_size: z.number().int().nullable(),
    package_price: z.number().nullable(),
    included_units: z.number().int().default(0),
    minimum_commitment: z.number().int().default(0),
    overage_allowed: z.boolean().default(true),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    agent: z
        .object({
        id: z.string(),
        name: z.string(),
    })
        .optional(),
    action: z
        .object({
        action_name: z.string(),
        action_type: z.string(),
    })
        .optional(),
    billable_signal: z
        .object({
        signal_name: z.string(),
        signal_code: z.string(),
        aggregation_type: z.string(),
        unit_label: z.string(),
    })
        .optional(),
});
export const PlanFeatureSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    plan_id: z.string(),
    feature_id: z.string(),
    type: PlanFeatureTypeSchema,
    billable_signal_id: z.string().nullable(),
    pricing_model: PricingModelSchema.nullable(),
    unit_price: z.number().nullable(),
    package_size: z.number().int().nullable(),
    package_price: z.number().nullable(),
    included_units: z.number().int().nullable(),
    minimum_commitment: z.number().int().nullable(),
    overage_allowed: z.boolean().default(true),
    metadata: MetadataSchema,
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
    feature: z
        .object({
        feature_name: z.string(),
        external_id: z.string().nullable(),
    })
        .optional(),
    billable_signal: z
        .object({
        signal_name: z.string(),
        signal_code: z.string(),
        aggregation_type: z.string(),
        unit_label: z.string(),
    })
        .optional(),
});
export const PricingTierSchema = z.object({
    id: z.string(),
    tenant_id: z.string(),
    environment_id: z.string(),
    parent_id: z.string(),
    parent_type: z.enum(['plan_agent_action', 'plan_feature']),
    tier_order: z.number().int(),
    start_unit: z.number().int(),
    end_unit: z.number().int().nullable(),
    price_per_unit: z.number(),
    flat_fee: z.number().nullable(),
    created_at: z.string().datetime(),
    updated_at: z.string().datetime(),
});
export const CreatePlanSchema = z.object({
    name: z.string().min(1).max(255),
    code: z.string().min(1).max(100),
    description: z.string().optional(),
    target_audience: TargetAudienceSchema.default('both'),
    currency: CurrencyCodeSchema.default('USD'),
    category: PlanCategorySchema.optional(),
    plan_type: PlanTypeSchema.optional(),
    billing_period: BillingPeriodSchema.default('monthly'),
    trial_available: z.boolean().default(false),
    trial_days: z.number().int().min(0).default(0),
    status: EntityStatusSchema.default('draft'),
    parent_plan_id: z.string().optional(),
    metadata: MetadataSchema.default({}),
});
export const CreatePlanChargeSchema = z.object({
    charge_name: z.string().min(1).max(255),
    charge_code: z.string().min(1).max(100),
    description: z.string().nullable().optional(),
    charge_type: ChargeTypeSchema,
    billing_frequency: BillingPeriodSchema.nullable().optional(),
    amount: z.number().positive(),
});
export const TierInputSchema = z.object({
    tier_order: z.number().int().min(1),
    start_unit: z.number().int().min(0),
    end_unit: z.number().int().positive().nullish(),
    price_per_unit: z.number().min(0),
    flat_fee: z.number().nullish(),
});
export const CreatePlanAgentActionSchema = z.object({
    agent_action_id: z.string(),
    billable_signal_id: z.string().optional(),
    pricing_model: PricingModelSchema,
    unit_price: z.number().optional(),
    package_size: z.number().int().positive().optional(),
    package_price: z.number().positive().optional(),
    included_units: z.number().int().min(0).default(0),
    minimum_commitment: z.number().int().min(0).default(0),
    overage_allowed: z.boolean().default(true),
    metadata: MetadataSchema.default({}),
    tiers: z.array(TierInputSchema).optional(),
});
const PlanFeatureBaseSchema = z.object({
    feature_id: z.string(),
    type: PlanFeatureTypeSchema,
    billable_signal_id: z.string().optional(),
    pricing_model: PricingModelSchema.optional(),
    unit_price: z.number().optional(),
    package_size: z.number().int().positive().optional(),
    package_price: z.number().positive().optional(),
    included_units: z.number().int().min(0).optional(),
    minimum_commitment: z.number().int().min(0).optional(),
    overage_allowed: z.boolean().default(true),
    metadata: MetadataSchema.default({}),
    tiers: z.array(TierInputSchema).optional(),
});
export const CreatePlanFeatureSchema = PlanFeatureBaseSchema.refine(data => {
    if (data.type === 'usage' && !data.pricing_model) {
        return false;
    }
    return true;
}, {
    message: 'Usage-based features must have a pricing model',
    path: ['pricing_model'],
})
    .refine(data => {
    if (data.type === 'usage' &&
        data.pricing_model === 'flat' &&
        (data.unit_price === null || data.unit_price === undefined)) {
        return false;
    }
    return true;
}, {
    message: 'Flat rate pricing requires a unit price',
    path: ['unit_price'],
})
    .refine(data => {
    if (data.type === 'usage' && data.pricing_model === 'package') {
        if (!data.package_size || !data.package_price) {
            return false;
        }
    }
    return true;
}, {
    message: 'Package pricing requires package size and price',
    path: ['package_price'],
})
    .refine(data => {
    if (data.type === 'usage' &&
        (data.pricing_model === 'tiered' || data.pricing_model === 'volume')) {
        if (!data.tiers || data.tiers.length === 0) {
            return false;
        }
    }
    return true;
}, {
    message: 'Tiered/volume pricing requires at least one pricing tier',
    path: ['tiers'],
});
export const CreatePricingTierSchema = z.object({
    parent_type: z.enum(['plan_agent_action', 'plan_feature']),
    tier_order: z.number().int().min(1),
    start_unit: z.number().int().min(0),
    end_unit: z.number().int().positive().optional(),
    price_per_unit: z.number().min(0),
    flat_fee: z.number().optional(),
});
export const UpdatePlanSchema = CreatePlanSchema.partial();
export const UpdatePlanChargeSchema = CreatePlanChargeSchema.partial();
export const UpdatePlanAgentActionSchema = CreatePlanAgentActionSchema.partial();
export const UpdatePlanFeatureSchema = PlanFeatureBaseSchema.partial();
export const UpdatePricingTierSchema = CreatePricingTierSchema.partial();
export const PlanQuerySchema = PaginationQuerySchema.extend({
    category: PlanCategorySchema.optional(),
    plan_type: PlanTypeSchema.optional(),
    status: EntityStatusSchema.optional(),
    target_audience: TargetAudienceSchema.optional(),
    billing_period: BillingPeriodSchema.optional(),
    currency: CurrencyCodeSchema.optional(),
    search: z.string().optional(),
    code: z.string().optional(),
});
export const PlanResponseSchema = PlanSchema.extend({
    charges: z.array(PlanChargeSchema).optional(),
    agent_actions: z
        .array(PlanAgentActionSchema.extend({
        pricing_tiers: z.array(PricingTierSchema).optional(),
    }))
        .optional(),
    features: z
        .array(PlanFeatureSchema.extend({
        pricing_tiers: z.array(PricingTierSchema).optional(),
    }))
        .optional(),
});
export const PaginatedPlansResponseSchema = z.object({
    data: z.array(PlanResponseSchema),
    pagination: z.object({
        page: z.number(),
        limit: z.number(),
        total: z.number(),
        total_pages: z.number(),
        has_next: z.boolean(),
        has_prev: z.boolean(),
    }),
});
export const PlanParamsSchema = z.object({
    id: z.string(),
});
export const PlanIdParamsSchema = z.object({
    planId: z.string(),
});
export const PlanChargeParamsSchema = z.object({
    planId: z.string(),
    chargeId: z.string(),
});
export const PlanAgentActionParamsSchema = z.object({
    planId: z.string(),
    actionId: z.string(),
});
export const PlanFeatureParamsSchema = z.object({
    planId: z.string(),
    featureId: z.string(),
});
export const PricingTierParamsSchema = z.object({
    planId: z.string(),
    parentId: z.string(),
    tierId: z.string(),
});
//# sourceMappingURL=index.js.map